<template>
  <form @submit.prevent="saveAddress">
    <div class="dialog-header">
      <h1>{{ title }}</h1>
    </div>
    <div class="dialog-content">
      <div class="row">
        <label>Organisatie</label>

        <input
          class="grey-field"
          type="text"
          v-model="newDeliveryAddress.company"
          name="company"
          spellcheck="false"
          maxlength="50"
          :class="{
            invalid: $v.newDeliveryAddress.company.$invalid
          }"
          autofocus
        />
      </div>

      <div class="row">
        <label>
          {{ custom.department.label ? custom.department.label : 'Afdeling' }}
        </label>

        <input
          class="grey-field"
          type="text"
          v-model="newDeliveryAddress.department"
          maxlength="50"
          :class="{
            invalid: $v.newDeliveryAddress.department.$invalid
          }"
        />
      </div>

      <div class="row">
        <label>T.a.v.</label>

        <input
          class="grey-field"
          type="text"
          v-model="newDeliveryAddress.name"
          required
          maxlength="35"
          :class="{
            invalid: $v.newDeliveryAddress.name.$invalid
          }"
        />
      </div>

      <div class="row zip-row">
        <label>Postcode</label>
        <input
          class="zip grey-field"
          type="text"
          v-model="newDeliveryAddress.zipCode"
          maxlength="9"
          :class="{
            invalid: $v.newDeliveryAddress.zipCode.$invalid
          }"
        />

        <label>Huisnr.</label>
        <input
          class="nr grey-field"
          type="text"
          v-model="newDeliveryAddress.number"
          @blur="getZipCode"
          maxlength="16"
          :class="{
            invalid: $v.newDeliveryAddress.number.$invalid
          }"
        />

        <label>Toev.</label>
        <input
          class="grey-field add"
          type="text"
          v-model="newDeliveryAddress.addition"
          maxlength="16"
        />
      </div>

      <div class="bouwplaats row">
        <select-box
          :selected="newDeliveryAddress.isConstructionSite"
          @action="setConstructionSite"
        ></select-box
        ><span @click="setConstructionSite" class="pointer"
          >Dit adres betreft een bouwplaats</span
        >
      </div>

      <div
        class="tel-bouwplaats row"
        v-if="newDeliveryAddress.isConstructionSite"
      >
        <label>Tel. nr. bouwplaats</label>
        <input
          class="grey-field"
          type="text"
          v-model="newDeliveryAddress.phone"
          maxlength="20"
        />
      </div>

      <div class="row">
        <label>Straat</label>

        <input
          class="grey-field"
          type="text"
          v-model="newDeliveryAddress.street"
          maxlength="35"
          :class="{
            invalid: $v.newDeliveryAddress.street.$invalid
          }"
          @input="
            newDeliveryAddress.street = newDeliveryAddress.street.replace(
              /[^a-zA-Z0-9 \-\.']/g,
              ''
            )
          "
        />
      </div>

      <div>
        <label>Plaats</label>

        <input
          class="grey-field"
          type="text"
          v-model="newDeliveryAddress.city"
          maxlength="30"
          :class="{
            invalid: $v.newDeliveryAddress.city.$invalid
          }"
        />
      </div>
      <div class="button-holder">
        <button
          class="pill-button secondary"
          type="button"
          @click="$emit('close')"
        >
          Annuleren
        </button>

        <button
          class="pill-button"
          type="submit"
          :disabled="$v.newDeliveryAddress.$invalid"
        >
          Bewaren
        </button>
      </div>
    </div>
  </form>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, numeric } from '@vuelidate/validators'
import { zipCode } from '@/api/api'
import cloneDeep from 'lodash-es/cloneDeep'
import { useAddressBookStore } from '@/stores/AddressBook.store'
import { useRootStore } from '@/stores/store'

const props = defineProps({
  address: Object,
  callback: Function,
  type: String
})

const emit = defineEmits(['close'])

const rootStore = useRootStore()
const custom = computed(() => rootStore.theme.customFields)
const title = computed(() => {
  if (props.type === 'new') return 'Nieuw bezorgadres'
  if (props.type === 'newPickup') return 'Nieuw ophaaladres'
  if (props.type === 'editPickup') return 'Ophaaladres bewerken'
  if (props.type === 'temp') return 'Bezorgadres instellen'
  return 'Bezorgadres bewerken'
})

const emptyAddress = {
  addition: '',
  city: '',
  company: '',
  deliveryAddress: true,
  department: '',
  id: 0,
  name: '',
  number: '',
  project: '',
  reference: '',
  street: '',
  zipCode: '',
  isConstructionSite: false
}

const newDeliveryAddress = ref(
  props.type === 'new' || props.type === 'temp' || props.type === 'newPickup'
    ? { ...emptyAddress }
    : { ...props.address }
)

const rules = computed(() => ({
  newDeliveryAddress: {
    company: { required },
    department: {
      required: requiredIf(() => custom.value.department.required)
    },
    zipCode: {
      required: requiredIf(() => !newDeliveryAddress.value.isConstructionSite)
    },
    number: {
      required: requiredIf(() => !newDeliveryAddress.value.isConstructionSite),
      numeric
    },
    name: { required },
    street: { required },
    city: { required }
  }
}))

const $v = useVuelidate(rules, { newDeliveryAddress })

function getZipCode() {
  zipCode(
    newDeliveryAddress.value.zipCode,
    newDeliveryAddress.value.number
  ).then(
    (address) => {
      let postcode = address.postcode.trim()
      newDeliveryAddress.value.zipCode = postcode.replace(
        /^(\d{4})([a-zA-Z]{2})$/,
        '$1 $2'
      )
      newDeliveryAddress.value.street = address.street
      newDeliveryAddress.value.city = address.city
    },
    () => {
      newDeliveryAddress.value.street = ''
      newDeliveryAddress.value.city = ''
    }
  )
}

function setConstructionSite() {
  newDeliveryAddress.value.isConstructionSite = !newDeliveryAddress.value
    .isConstructionSite
}

function saveAddress() {
  if (props.type === 'temp') {
    newDeliveryAddress.value.id = 0
    props.callback(newDeliveryAddress.value)
    emit('close')

    return
  }

  const addressBookStore = useAddressBookStore()

  if (
    !newDeliveryAddress.value.isConstructionSite &&
    newDeliveryAddress.value.phone
  ) {
    newDeliveryAddress.value.phone = ''
  }

  const newAddress = cloneDeep(newDeliveryAddress.value)

  if (newAddress.id === 0) {
    delete newAddress.id
    delete newAddress.deliveryAddress
    delete newAddress.project
    delete newAddress.reference
    addressBookStore
      .addDeliveryAddress(newAddress)
      .then((address) => {
        props.callback(address)
        emit('close')
      })
      .catch(() => {
        console.log('error')
      })
  } else {
    addressBookStore.editDeliveryAddress(newAddress).then((address) => {
      props.callback(address)
      emit('close')
    })
  }
}
</script>

<style lang="scss" scoped>
label {
  width: 75px;
  line-height: 34px;
}

.bouwplaats {
  padding-left: 77px;
}

.tel-bouwplaats {
  padding-left: 77px;
  label {
    width: 130px;
  }
  input[type='text'] {
    width: 148px;
  }
}

input[type='text'] {
  width: 285px;
}

.button-holder {
  padding-top: 25px;
}

.secondary {
  margin-right: 5px;
}

.row {
  padding-bottom: 15px;
}

.zip-row {
  display: flex;

  label:not(:first-child) {
    width: auto;
    margin-left: 7px;
  }

  .zip {
    width: 78px;
  }

  .nr {
    width: 54px;
  }

  .add {
    width: 48px;
  }
}
</style>
